/**
 * HeaderControls Component
 *
 * Header section for SPLH Dashboard with Date Range and Comparison buttons.
 * Displays buttons showing current selections with date ranges below.
 *
 * Features:
 * - Date Range button showing current preset (opens custom date modal)
 * - Comparison button showing current mode (opens comparison modal)
 * - Date range displays below buttons
 * - Purple theme matching dashboard design
 *
 * @module pages/common/dashboards/SPLHDashboard/components/HeaderControls
 */

import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Box, Typography, Chip } from '@mui/material';
import {
  CalendarToday as CalendarIcon,
  CompareArrows as CompareIcon,
} from '@mui/icons-material';
import AdminButton from 'components/common/AdminButton';
import DateRangeSelectionModal from 'components/ReportHeader/modals/DateRangeSelectionModal';
import ComparisonModal from '../ComparisonModal';
import { formatDateRangeForDisplay } from '../../utils/dateUtils';
import { COMPARISON_MODES } from '../../hooks/useSPLHDataV2';

/**
 * Label mapping for date range presets
 * @constant {Object}
 */
const PRESET_LABELS = {
  today: 'Today',
  yesterday: 'Yesterday',
  last7days: 'Last 7 Days',
  last30days: 'Last 30 Days',
  mtd: 'Month to Date',
  custom: 'Custom',
};

/**
 * Label mapping for comparison modes
 * @constant {Object}
 */
const COMPARISON_MODE_LABELS = {
  [COMPARISON_MODES.PREVIOUS_PERIOD]: 'Previous Period',
  [COMPARISON_MODES.PREVIOUS_MONTH]: 'Previous Month',
  [COMPARISON_MODES.PREVIOUS_YEAR]: 'Previous Year',
};

/**
 * HeaderControls Component
 *
 * @param {Object} props - Component props
 * @param {string} props.selectedPreset - Currently selected date range preset
 * @param {Object} props.dateRange - Current date range { startDate, endDate }
 * @param {string} props.comparisonMode - Currently selected comparison mode
 * @param {Object} props.comparisonRange - Comparison date range { startDate, endDate }
 * @param {Function} props.onDateRangeChange - Callback when date range is selected (includes preset)
 * @param {Function} props.onComparisonModeChange - Callback when comparison mode changes
 * @param {boolean} [props.loading=false] - Whether data is loading
 * @returns {React.Component} HeaderControls component
 */
const HeaderControls = ({
  selectedPreset,
  dateRange,
  comparisonMode,
  comparisonRange,
  onDateRangeChange,
  onComparisonModeChange,
  loading = false,
}) => {
  // Modal states
  const [isDateModalOpen, setIsDateModalOpen] = useState(false);
  const [isComparisonModalOpen, setIsComparisonModalOpen] = useState(false);

  /**
   * Handles date range selection from modal
   * @param {Object} selection - Selected date range { from, to, preset }
   */
  const handleDateRangeSubmit = useCallback(
    (selection) => {
      setIsDateModalOpen(false);
      if (selection?.from && selection?.to) {
        onDateRangeChange({
          startDate: selection.from,
          endDate: selection.to,
          preset: selection.preset || 'custom',
        });
      }
    },
    [onDateRangeChange]
  );

  /**
   * Handles comparison mode selection from modal
   * @param {string} mode - Selected comparison mode
   */
  const handleComparisonSubmit = useCallback(
    (mode) => {
      setIsComparisonModalOpen(false);
      if (mode) {
        onComparisonModeChange(mode);
      }
    },
    [onComparisonModeChange]
  );

  /**
   * Gets the display label for the date range button
   * @returns {string} Display label
   */
  const getDateRangeButtonLabel = () => {
    return PRESET_LABELS[selectedPreset] || 'Last 7 Days';
  };

  /**
   * Gets the display label for the comparison button
   * @returns {string} Display label
   */
  const getComparisonButtonLabel = () => {
    return COMPARISON_MODE_LABELS[comparisonMode] || 'Previous Period';
  };

  /**
   * Formats the current date range for display
   * @returns {string} Formatted date range
   */
  const getCurrentRangeDisplay = () => {
    return formatDateRangeForDisplay(dateRange?.startDate, dateRange?.endDate);
  };

  /**
   * Formats the comparison date range for display
   * @returns {string} Formatted comparison range
   */
  const getComparisonRangeDisplay = () => {
    if (!comparisonRange?.startDate || !comparisonRange?.endDate) {
      return 'Calculating...';
    }
    return formatDateRangeForDisplay(comparisonRange.startDate, comparisonRange.endDate);
  };

  return (
    <Box>
      {/* Buttons row */}
      <Box sx={{ display: 'flex', gap: 2, mb: 1 }}>
        {/* Date Range Button */}
        <AdminButton
          variant="outlined"
          size="small"
          startIcon={<CalendarIcon />}
          onClick={() => setIsDateModalOpen(true)}
          disabled={loading}
        >
          {getDateRangeButtonLabel()}
        </AdminButton>

        {/* Comparison Mode Button */}
        <AdminButton
          variant="outlined"
          size="small"
          startIcon={<CompareIcon />}
          onClick={() => setIsComparisonModalOpen(true)}
          disabled={loading}
        >
          {getComparisonButtonLabel()}
        </AdminButton>
      </Box>

      {/* Date ranges display row */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        {/* Current range */}
        <Chip
          label={getCurrentRangeDisplay()}
          size="small"
          sx={{
            backgroundColor: 'rgba(124, 58, 237, 0.08)',
            color: '#7C3AED',
            fontWeight: 500,
            '& .MuiChip-label': {
              fontSize: '0.75rem',
            },
          }}
        />

        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8125rem' }}>
          vs
        </Typography>

        {/* Comparison range */}
        <Chip
          label={getComparisonRangeDisplay()}
          size="small"
          sx={{
            backgroundColor: 'rgba(124, 58, 237, 0.08)',
            color: '#7C3AED',
            fontWeight: 500,
            '& .MuiChip-label': {
              fontSize: '0.75rem',
            },
          }}
        />

        {loading && (
          <Typography variant="caption" color="text.secondary">
            Loading...
          </Typography>
        )}
      </Box>

      {/* Custom Date Range Modal */}
      <DateRangeSelectionModal
        open={isDateModalOpen}
        onClose={() => setIsDateModalOpen(false)}
        currentDateRange={dateRange}
        currentPreset={selectedPreset}
        onSubmit={handleDateRangeSubmit}
      />

      {/* Comparison Mode Modal */}
      <ComparisonModal
        open={isComparisonModalOpen}
        onClose={() => setIsComparisonModalOpen(false)}
        currentMode={comparisonMode}
        onSubmit={handleComparisonSubmit}
      />
    </Box>
  );
};

HeaderControls.propTypes = {
  /** Currently selected date range preset */
  selectedPreset: PropTypes.string.isRequired,
  /** Current date range { startDate, endDate } */
  dateRange: PropTypes.shape({
    startDate: PropTypes.string,
    endDate: PropTypes.string,
  }).isRequired,
  /** Currently selected comparison mode */
  comparisonMode: PropTypes.string.isRequired,
  /** Comparison date range { startDate, endDate } */
  comparisonRange: PropTypes.shape({
    startDate: PropTypes.string,
    endDate: PropTypes.string,
  }),
  /** Callback when date range is selected (includes preset) */
  onDateRangeChange: PropTypes.func.isRequired,
  /** Callback when comparison mode changes */
  onComparisonModeChange: PropTypes.func.isRequired,
  /** Whether data is loading */
  loading: PropTypes.bool,
};

export default HeaderControls;
